import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Skeleton,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Snackbar,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import {
  useRolesGetRoles,
  useRolesCreateRole,
  useRolesUpdateRole,
  useRolesShareRole,
  useRolesUnshareRole,
  getRolesGetRolesQueryKey
} from '@/api-gen/api';
import type { CreateRoleDto, RoleDto } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';
import {
  isUserSuperAdminSelector,
  isUserAnyAdminSelector,
  CurrentUserSelector
} from 'app/selectors/userSelectors';
import { companiesSelector } from 'app/selectors/companiesSelector';
import { addInfo, addError } from 'app/actions/snackbar/snackbar';
import RoleDetailDialog from './RoleDetailDialog';

interface Props {
  value?: number | null;
  onChange: (roleId: number | null) => void;
  langCode: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  scenarioCompanyId?: number;
}

const ScenarioPersonaRoleSelector: React.FC<Props> = ({
  value,
  onChange,
  langCode,
  disabled = false,
  size = 'small',
  scenarioCompanyId
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const isAnyAdmin = useSelector(isUserAnyAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const companies = useSelector(companiesSelector);

  const contextCompanyId = scenarioCompanyId ?? currentUser?.companyId;
  const contextCompanyName = useMemo(
    () => companies?.find((c) => c.id === contextCompanyId)?.name || `#${contextCompanyId}`,
    [companies, contextCompanyId]
  );

  // Can this user create/edit roles?
  const canManageRoles = isSuperAdmin || isAnyAdmin;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const chipRef = useRef<HTMLDivElement>(null);

  // Edit/create popover state
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Undo state for share+assign
  const [undoState, setUndoState] = useState<{
    timeoutId: ReturnType<typeof setTimeout>;
    roleId: number;
    roleName: string;
    previousRoleId: number | null;
  } | null>(null);

  // Fetch roles with companyId context for superadmins
  const rolesQueryParams = useMemo(() => {
    const params: { langCode?: string; companyId?: number } = { langCode };
    if (isSuperAdmin && contextCompanyId) {
      params.companyId = contextCompanyId;
    }
    return params;
  }, [langCode, isSuperAdmin, contextCompanyId]);

  const { data, isLoading } = useRolesGetRoles(
    rolesQueryParams,
    { query: { enabled: !!langCode, staleTime: 5 * 60 * 1000 } }
  );

  const roles: RoleDto[] = useMemo(() => {
    return (data?.data as RoleDto[]) || (data as unknown as RoleDto[]) || [];
  }, [data]);

  const selectedRole = useMemo(() => roles.find((r) => r.id === value), [roles, value]);
  const open = Boolean(anchorEl);

  // Split roles into org roles and other roles (superadmin only)
  const { orgRoles, otherRoles } = useMemo(() => {
    if (!isSuperAdmin) {
      // Non-superadmins see all roles as "org roles" (API already filters)
      return { orgRoles: roles, otherRoles: [] as RoleDto[] };
    }
    // Superadmin: roles with access != null are shared with context org
    const org: RoleDto[] = [];
    const other: RoleDto[] = [];
    for (const role of roles) {
      if (role.access != null) {
        org.push(role);
      } else {
        other.push(role);
      }
    }
    return { orgRoles: org, otherRoles: other };
  }, [roles, isSuperAdmin]);

  // Filter by search
  const filterRoles = useCallback(
    (list: RoleDto[]) => {
      if (!searchQuery.trim()) return list;
      const q = searchQuery.toLowerCase().trim();
      return list.filter(
        (r) =>
          r.name?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q)
      );
    },
    [searchQuery]
  );

  const filteredOrgRoles = useMemo(() => filterRoles(orgRoles), [filterRoles, orgRoles]);
  const filteredOtherRoles = useMemo(() => filterRoles(otherRoles), [filterRoles, otherRoles]);

  const createRoleMutation = useRolesCreateRole();
  const updateRoleMutation = useRolesUpdateRole();
  const shareRoleMutation = useRolesShareRole();
  const unshareRoleMutation = useRolesUnshareRole();

  const invalidateRoles = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getRolesGetRolesQueryKey(rolesQueryParams) });
  }, [queryClient, rolesQueryParams]);

  // --- Handlers ---

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && !isLoading) {
      setAnchorEl(event.currentTarget);
      setSearchQuery('');
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
  }, []);

  const handleSelect = useCallback((roleId: number) => {
    onChange(roleId);
    handleClose();
  }, [onChange, handleClose]);

  const handleEditClick = useCallback((event: React.MouseEvent, role: RoleDto) => {
    event.stopPropagation();
    setEditingRole(role);
    setIsPopoverOpen(true);
  }, []);

  const handleAddNewClick = () => {
    setEditingRole(null);
    setIsPopoverOpen(true);
    handleClose();
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
    setEditingRole(null);
  };

  const handleSave = async (name: string, description: string | null) => {
    setIsSaving(true);
    try {
      if (editingRole?.id) {
        // Update existing role
        await updateRoleMutation.mutateAsync({
          id: editingRole.id,
          data: { name, description }
        });
        dispatch(
          addInfo({
            infoText: translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleUpdated),
              { roleName: name }
            )
          })
        );
      } else {
        // Create new role
        const createData: CreateRoleDto = {
          name,
          description,
          languageCode: langCode
        };
        if (isSuperAdmin && contextCompanyId) {
          createData.companyId = contextCompanyId;
        }
        const response = await createRoleMutation.mutateAsync({ data: createData });
        const newRole = (response?.data as RoleDto) || (response as unknown as RoleDto);
        dispatch(
          addInfo({
            infoText: translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleCreated),
              { roleName: name }
            )
          })
        );
        // Auto-assign the newly created role
        if (newRole?.id) {
          onChange(newRole.id);
        }
      }
      invalidateRoles();
      handlePopoverClose();
    } catch (error) {
      dispatch(
        addError({
          errorText: translate(
            editingRole?.id
              ? nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleUpdateError)
              : nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleCreateError)
          )
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Superadmin: share+assign an unshared role
  const handleShareAndAssign = async (role: RoleDto) => {
    if (!role.id || !contextCompanyId) return;

    const previousRoleId = value ?? null;

    try {
      // Share with CanRead
      await shareRoleMutation.mutateAsync({
        id: role.id,
        data: { companyId: contextCompanyId, accessLevel: AccessLevel.CanRead }
      });

      // Assign the role
      onChange(role.id);
      handleClose();
      invalidateRoles();

      // Toast with undo — store roleName at share time to avoid stale lookups after refetch
      if (undoState?.timeoutId) {
        clearTimeout(undoState.timeoutId);
      }
      const timeoutId = setTimeout(() => {
        setUndoState(null);
      }, 8000);

      setUndoState({ timeoutId, roleId: role.id, roleName: role.name || '', previousRoleId });

      // Note: We use a local Snackbar for this action to support the "Undo" button
    } catch (error) {
      dispatch(
        addError({
          errorText: translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShareError)
          )
        })
      );
    }
  };

  const handleUndo = () => {
    if (undoState) {
      const { roleId, previousRoleId, timeoutId } = undoState;
      // Clear timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Revert selection
      onChange(previousRoleId);
      // Undo company sharing if we have a valid company context
      if (contextCompanyId) {
        unshareRoleMutation.mutate(
          {
            id: roleId,
            data: { companyId: contextCompanyId }
          },
          {
            onError: () => {
              dispatch(
                addError({
                  errorText: translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShareError)
                  )
                })
              );
            },
            onSettled: () => {
              invalidateRoles();
            }
          }
        );
      }
      // Close snackbar
      setUndoState(null);
    }
  };

  const handleUndoClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setUndoState(null);
  };

  const canEditRole = useCallback((role: RoleDto): boolean => {
    if (isSuperAdmin) return true;
    if (!isAnyAdmin) return false;
    return role.access === AccessLevel.CanEdit;
  }, [isAnyAdmin, isSuperAdmin]);

  useEffect(() => {
    return () => {
      if (undoState?.timeoutId) {
        clearTimeout(undoState.timeoutId);
      }
    };
  }, [undoState]);

  // --- Render ---

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width={120}
        height={size === 'small' ? 24 : 32}
        sx={{ borderRadius: 12 }}
      />
    );
  }

  const chipLabel = selectedRole
    ? selectedRole.name
    : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.none));
  const isNoneSelected = !selectedRole;

  const renderRoleMenuItem = (role: RoleDto, showEditIcon: boolean, isOtherRole = false) => (
    <MenuItem
      key={role.id}
      onClick={() => {
        if (isOtherRole) {
          handleShareAndAssign(role);
        } else {
          handleSelect(role.id!);
        }
      }}
      selected={!isOtherRole && value === role.id}
      sx={{
        py: 0.75,
        px: 2,
        opacity: isOtherRole ? 0.7 : 1,
        '&.Mui-selected': {
          backgroundColor: 'primary.50',
          color: 'primary.main',
          fontWeight: 500
        }
      }}
    >
      <ListItemText
        primary={
          <Typography
            variant="body2"
            sx={{
              fontWeight: !isOtherRole && value === role.id ? 500 : 400,
              color: isOtherRole ? 'text.secondary' : (!isOtherRole && value === role.id ? 'primary.main' : 'text.primary')
            }}
          >
            {role.name}
          </Typography>
        }
        secondary={
          isOtherRole ? (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
              {translate(
                nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.shareAndAssign),
                { companyName: contextCompanyName }
              )}
            </Typography>
          ) : role.description ? (
            <Tooltip title={role.description} placement="right" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 180,
                  fontSize: '0.7rem'
                }}
              >
                {role.description}
              </Typography>
            </Tooltip>
          ) : undefined
        }
      />
      {showEditIcon && canEditRole(role) && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => handleEditClick(e, role)}
            sx={{ ml: 1 }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </MenuItem>
  );

  const hasResults = filteredOrgRoles.length > 0 || filteredOtherRoles.length > 0;

  return (
    <Box>
      <Chip
        ref={chipRef}
        label={chipLabel}
        onClick={handleClick}
        disabled={disabled}
        size={size}
        variant="outlined"
        color={isNoneSelected ? 'default' : 'primary'}
        icon={<PersonIcon />}
        deleteIcon={<ExpandMoreIcon />}
        onDelete={handleClick}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiChip-deleteIcon': {
            color: 'inherit',
            marginLeft: 1,
            marginRight: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out'
          },
          '&:hover': disabled
            ? {}
            : {
                backgroundColor: 'action.hover',
                borderColor: isNoneSelected ? 'action.disabled' : 'primary.main'
              },
          borderColor: isNoneSelected ? 'divider' : 'primary.main',
          color: isNoneSelected ? 'text.secondary' : 'primary.main',
          fontWeight: isNoneSelected ? 400 : 500,
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          height: size === 'small' ? 24 : 32,
          minWidth: size === 'small' ? 60 : 80,
          paddingLeft: size === 'small' ? 0.5 : 2,
          paddingRight: size === 'small' ? 0.5 : 2
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            minWidth: 240,
            maxWidth: 320,
            maxHeight: 400,
            mt: 0.5,
            boxShadow: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        {/* Search field */}
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <TextField
            placeholder={translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.searchRoles)
            )}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.85rem'
              }
            }}
            onKeyDown={(e) => {
              // Prevent Menu from handling key events on the search field
              e.stopPropagation();
            }}
          />
        </Box>

        {/* Org roles */}
        {filteredOrgRoles.map((role) => renderRoleMenuItem(role, canManageRoles, false))}

        {/* Other roles section (superadmin only) */}
        {isSuperAdmin && filteredOtherRoles.length > 0 && (
          <>
            {filteredOrgRoles.length > 0 && <Divider sx={{ my: 0.5 }} />}
            <MenuItem disabled sx={{ py: 0.5 }}>
              <ListItemText>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                  <LanguageIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.otherRoles))}
                </Typography>
              </ListItemText>
            </MenuItem>
            {filteredOtherRoles.map((role) => renderRoleMenuItem(role, canManageRoles, true))}
          </>
        )}

        {/* No results */}
        {!hasResults && (
          <MenuItem disabled>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.noRolesFound))}
                </Typography>
              }
            />
          </MenuItem>
        )}

        {/* Add new role */}
        {canManageRoles && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleAddNewClick} sx={{ py: 1, px: 2 }}>
              <AddIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
              <Typography variant="body2" color="primary.main" fontWeight={500}>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.addNewRole))}
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Edit/Create Dialog */}
      <RoleDetailDialog
        open={isPopoverOpen}
        onClose={handlePopoverClose}
        onSave={handleSave}
        role={editingRole}
        isSaving={isSaving}
        scenarioCompanyId={contextCompanyId}
        scenarioCompanyName={contextCompanyName}
        canManageSharing={isSuperAdmin}
        onSharingChanged={invalidateRoles}
      />

      {/* Undo Snackbar for Share+Assign */}
      <Snackbar
        open={!!undoState}
        autoHideDuration={8000}
        onClose={handleUndoClose}
        message={
          undoState
            ? translate(
                nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShared),
                { roleName: undoState.roleName, companyName: contextCompanyName }
              )
            : ''
        }
        action={
          <Button color="secondary" size="small" onClick={handleUndo}>
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleSharedUndo))}
          </Button>
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ScenarioPersonaRoleSelector;
